import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { setUser } from '../../../../../app/store/slices/auth/authSlice';
import { updateUserById } from '../../../../../app/store/slices/auth/authSlice';
import { verifyUserKyc } from '../../../../../app/store/slices/user/user';
import { PAN_NUMBER, GST_REGEX } from '../../../../../utils/validations';
import { errorToast, successToastTopRight } from '../../../../../utils/toasts/toasts';

export default function useAddTaxInvoiceDetails({ onSuccess }) {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [loading, setLoading] = useState(false);
    const { countries, allStates, allCities } = useSelector((state) => state.cities);

    const initialValues = {
        panNumber: '',
        gstNumber: '',
        isGstRegistered: false,
        address: '',
        city: '',
        state: '',
        country: countries[0] || null,
        pinCode: '',
        isTermsAccepted: false
    };


    const validationSchema = Yup.object({
        panNumber: Yup.string()
            .required('PAN Number is required')
            .matches(PAN_NUMBER, 'Invalid PAN Number'),
        gstNumber: Yup.string().when('isGstRegistered', {
            is: true,
            then: (schema) =>
                schema.required('GST Number is required').matches(GST_REGEX, 'Invalid GST Number'),
            otherwise: (schema) => schema.notRequired()
        }),
        isGstRegistered: Yup.boolean(),
        address: Yup.string().required('Address is required'),
        city: Yup.mixed().required('City is required'),
        state: Yup.mixed().required('State is required'),
        pinCode: Yup.string()
            .required('Pin Code is required')
            .matches(/^\d{6}$/, 'Pin Code must be a 6-digit number'),
        country: Yup.mixed().required('Country is required'),
        isTermsAccepted: Yup.boolean().oneOf([true], 'You must accept the terms and conditions')
    });


    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const invoicePayload = {
                    user_id: user.id,
                    pan_number: values.panNumber,
                    gst_number: values.isGstRegistered ? values.gstNumber : '',
                }

                const payload = {
                    // pan_number: values.panNumber,
                    // gst_number: values.isGstRegistered ? values.gstNumber : '',
                    address: values.address,
                    invoice_city_id: values.city.id || '',
                    state_id: values.state.id || '',
                    country_id: countries[0]?.id,
                    pincode: values.pinCode
                };

                const panVerification = await dispatch(
                    verifyUserKyc(invoicePayload)
                ).unwrap();

                await dispatch(
                    updateUserById({
                        id: user.id,
                        data: payload
                    })
                ).unwrap();


                dispatch(
                    setUser({
                        ...user,
                        address: values.address,
                        city_id: values.city.id || '',
                        state_id: values.state.id || '',
                        pincode: values.pinCode,
                        verification: panVerification?.data
                    })
                );

                if (onSuccess) onSuccess();

                formik.setFieldValue('isEditable', false);
                successToastTopRight('Tax invoice details updated successfully');
            } catch (error) {
                // console.error("Error updating tax invoice details:", error);
                errorToast(error?.message);
            } finally {
                setLoading(false);
            }
        }
    });

    // console.log('Formik Values:', formik.values);

    const filterCitiesAndStates = useMemo(() => {
        const gstNumber = formik.values.gstNumber;

        if (gstNumber && gstNumber.length >= 2 && formik.values.isGstRegistered) {
            const stateCode = gstNumber.substring(0, 2);

            const matchedState = allStates?.find(state => state.gst_code === stateCode);
            const matchedCities = allCities?.filter(city => city.state_id === (matchedState ? matchedState.id : null));

            return {
                cities: matchedCities || [],
                state: matchedState ? [matchedState] : []
            };
        } else if (!formik.values.isGstRegistered && formik.values.city) {

            const matchedState = allStates?.find(state => state.id === formik.values.city.state_id);
            return {
                state: matchedState ? [matchedState] : [],
                cities: allCities
            };
        }
        else {
            return {
                state: allStates || [],
                cities: allCities || []
            }
        }
    }, [formik.values.gstNumber, formik.values.isGstRegistered, formik.values.city]);

    return {
        formik,
        loading,
        filterCitiesAndStates
    };
}